// ─────────────────────────────────────────────────────────────────────────────
// usePermissions — the single client-side gate for RBAC UI.
//
// Reads the logged-in user's materialized permission rows (hydrated from
// GET /auth/me by AuthProvider) and answers "can this user see / write this
// module?". The role → module → level matrix lives on the backend
// (config/rbac.ts); this hook only interprets the user's own effective levels,
// so it automatically respects any per-user override an Admin has set.
//
//   canAccess(module) → module is visible at all      (drives sidebar + routes)
//   canWrite(module)  → show create / edit / delete    (drives CRUD controls)
//   can(module, min)  → generic level comparison
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo } from "react";
import { useAuthState } from "./AuthProvider";

// Ascending access levels — index = rank. Mirrors LEVELS in backend config/rbac.ts.
export const ACCESS_LEVELS = ["none", "read", "read_write", "full_access"];

// The 5 RBAC modules, keyed the same way the backend does.
export const MODULES = {
  USER_MANAGEMENT: "user_management",
  POLICY_MANAGEMENT: "policy_management",
  NODE_MONITORING: "node_monitoring",
  ANALYTICS: "analytics",
  SETTINGS: "settings",
};

function meets(level, minimum) {
  return ACCESS_LEVELS.indexOf(level) >= ACCESS_LEVELS.indexOf(minimum);
}

export function usePermissions() {
  const { user } = useAuthState();

  return useMemo(() => {
    // Admin bypasses every check server-side; mirror that here so the UI never
    // depends on Admin rows being materialized.
    const isAdmin = user?.role === "Admin";

    const byModule = {};
    for (const row of user?.permissions ?? []) {
      byModule[row.module] = row.accessLevel;
    }

    const accessLevel = (module) =>
      isAdmin ? "full_access" : byModule[module] ?? "none";

    const can = (module, minimum) => meets(accessLevel(module), minimum);

    return {
      accessLevel,
      can,
      canAccess: (module) => accessLevel(module) !== "none",
      canWrite: (module) => can(module, "read_write"),
    };
  }, [user]);
}
